import React, { useEffect, useState } from "react";
import { Button } from "../Button";
import TextInput from "../TextInput";
import Result from "../Result";
import './umbrella.css';
import { parseNumber, formatNumber, postHeightToParent } from "../common-helpers";
import { calc } from './helpers';

export const Umbrella: React.FC = () => {
  const [networth, setNetWorth] = useState<string>('');
  const [equity, setEquity] = useState<string>('');
  const [retirement, setRetirement] = useState<string>('');
  const [liability, setLiability] = useState<string>('');
  const [result, setResult] = useState<string>('');
  const [showResult, setShowResult] = useState(false);


  const onNetWorthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNetWorth(getCurrencyValue(e.target.value));
  };
  const onEquityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEquity(getCurrencyValue(e.target.value));
  };
  const onRetirementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRetirement(getCurrencyValue(e.target.value));
  };
  const onLiabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLiability(getCurrencyValue(e.target.value));
  };

  const getCurrencyValue = (fieldValue: string) => {
    const amount: string = parseNumber(fieldValue).toString();

    return amount;
  }

  const onSubmit = () => {
    const result = calc(networth, equity, retirement, liability);
    setShowResult(true);
    setResult(result.toString());
  };

  const onReset = () => {
    setShowResult(false);
    setNetWorth("");
    setEquity("");
    setRetirement("");
    setLiability("");
  }

  useEffect(() => {
    postHeightToParent(document.body.scrollHeight);
  }, [showResult]);

  return (
    <div className="container__umbrellaCalc">
      <form className="childTaxCredit-form" name="umbrella" id="umbrella">
        <header className="umbrellaCalc-title">Umbrella Insurance Calculator</header>

        <div className="umbrellaCalc-input-block">
          <label htmlFor="umbrellaCalc_networth">Net Worth</label>
          <div className="umbrellaCalc-input-group">
            <span className="umbrellaCalc-input-prefix">$</span>
            <TextInput
              id="umbrellaCalc_networth"
              name="networth"
              inputMode="numeric"
              fieldClass="umbrellaCalc-input"
              onChange={onNetWorthChange}
              value={formatNumber(networth)}
              data-testid="umbrellaCalc-networth"
            />
          </div>
        </div>
        <p className="umbrellaCalc-input-explanation">This is your assets minus your debts; you can quickly <a href="https://www.kiplinger.com/investing/wealth-management/603443/net-worth-calculator" target="blank">calculate your net worth here</a>.</p>

        <div className="umbrellaCalc-input-block">
          <label htmlFor="umbrellaCalc_equity">Home Equity</label>
          <div className="umbrellaCalc-input-group">
            <span className="umbrellaCalc-input-prefix">$</span>
            <TextInput
              id="umbrellaCalc_equity"
              name="equity"
              inputMode="numeric"
              fieldClass="umbrellaCalc-input"
              onChange={onEquityChange}
              value={formatNumber(equity)}
              data-testid="umbrellaCalc-equity"
            />
          </div>
        </div>
        <p className="umbrellaCalc-input-explanation">This is the amount of equity you currently have in your home, <em>up to your state's exemption limit</em>. Federal and state exemption laws (also known as bankruptcy exemptions) prevent judgment creditors from seizing certain types and amounts of property, and every state except Pennsylvania and New Jersey provides a homestead exemption that protects at least some of your equity in your primary residence, but the amount varies greatly. Find <a href="http://www.nolo.com/legal-encyclopedia/bankruptcy-exemptions-state" target="_blank" rel="noreferrer">your state's homestead exemption here</a>.</p>

        <div className="umbrellaCalc-input-block">
          <label htmlFor="umbrellaCalc_retirement">Value of Retirement Plans</label>
          <div className="umbrellaCalc-input-group">
            <span className="umbrellaCalc-input-prefix">$</span>
            <TextInput
              id="umbrellaCalc_retirement"
              name="retirement"
              inputMode="numeric"
              fieldClass="umbrellaCalc-input"
              onChange={onRetirementChange}
              value={formatNumber(retirement)}
              data-testid="umbrellaCalc-retirement"
            />
          </div>
        </div>
        <p className="umbrellaCalc-input-explanation">A judgment creditor also can't go after any employer-provided retirement or pension plan -- including <strong>401(k)s</strong> and <strong>deferred-compensation or profit-sharing plans</strong> -- that's qualified under ERISA, the Employee Retirement Income Security Act. Also, states generally exempt non-qualified retirement accounts (including <strong>traditional IRAs, Roth IRAs, SIMPLE IRAs, and SEP, Keogh and 403(b) plans</strong>), but specifics vary.</p>

        <div className="umbrellaCalc-input-block">
          <label htmlFor="liability">Limit of Your Liability Coverage</label>
          <div className="umbrellaCalc-input-group">
            <span className="umbrellaCalc-input-prefix">$</span>
            <TextInput
              id="umbrellaCalc_liability"
              name="liability"
              inputMode="numeric"
              fieldClass="umbrellaCalc-input"
              onChange={onLiabilityChange}
              value={formatNumber(liability)}
              data-testid="umbrellaCalc-liability"
            />
          </div>
        </div>
        <p className="umbrellaCalc-input-explanation">This is your auto liability coverage for injury to one person or your homeowners liability coverage, whichever is less.</p>

        { showResult && (
          <div className="umbrellaCalc-result-block">
            <div className="umbrellaCalc-result-title">We recommend this much umbrella coverage:</div>
            <Result
              id="umbrellaCalc-result"
              classNames="umbrellaCalc-result-value"
              result={`$` + formatNumber(result)}
            />
          </div>
        )}

        <p>
          <Button
            label="Submit"
            className="primary-button umbrellaCalc-btn"
            onClickHandler={onSubmit}
            data-testid="umbrellaCalc-calculate"
          />
          <Button
            label="Clear Form"
            className="default-button umbrellaCalc-btn"
            onClickHandler={onReset}
            data-testid="umbrellaCalc-clear"
          />
        </p>

      </form>
    </div>
  );
}
